import { getEnv } from '../utils/env';
import { authenticatedFetch, getAuthHeaders, hasStoredAuthSession, isStoredAdminUser } from './authApi';

type RequestHeadersInput = HeadersInit | undefined;

type ProtectedAdminErrorReason = 'config' | 'unauthenticated' | 'forbidden' | 'request_failed';

export class ProtectedAdminRequestError extends Error {
  status: number;
  reason: ProtectedAdminErrorReason;

  constructor(message: string, options: { status: number; reason: ProtectedAdminErrorReason }) {
    super(message);
    this.name = 'ProtectedAdminRequestError';
    this.status = options.status;
    this.reason = options.reason;
  }
}

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');

const getApiKey = (): string => getEnv('VITE_AI_API_KEY', '');

const mergeHeaders = (...entries: RequestHeadersInput[]): Headers => {
  const headers = new Headers();
  entries.forEach((entry) => {
    if (!entry) return;
    new Headers(entry).forEach((value, key) => {
      headers.set(key, value);
    });
  });
  return headers;
};

const getAccessState = () => {
  const baseUrl = toApiBaseUrl();
  const apiKey = getApiKey();
  const hasSession = hasStoredAuthSession();
  const isAdmin = isStoredAdminUser();

  if (!baseUrl || !apiKey) {
    throw new ProtectedAdminRequestError('Admin API is niet correct geconfigureerd.', {
      status: 500,
      reason: 'config',
    });
  }

  if (!hasSession) {
    throw new ProtectedAdminRequestError('Je bent niet ingelogd of je sessie is verlopen.', {
      status: 401,
      reason: 'unauthenticated',
    });
  }

  if (!isAdmin) {
    throw new ProtectedAdminRequestError('Alleen administrators mogen deze actie uitvoeren.', {
      status: 403,
      reason: 'forbidden',
    });
  }

  return { baseUrl, apiKey };
};

const parseResponseBody = async (response: Response): Promise<unknown> => {
  if (response.bodyUsed) {
    return null;
  }
  const raw = await response.text();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

const extractMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    if (typeof record.detail === 'string' && record.detail.trim().length > 0) return record.detail;
    if (typeof record.message === 'string' && record.message.trim().length > 0) return record.message;
    if (typeof record.error === 'string' && record.error.trim().length > 0) return record.error;
    if (record.detail && typeof record.detail === 'object') {
      const detail = record.detail as Record<string, unknown>;
      if (typeof detail.message === 'string' && detail.message.trim().length > 0) return detail.message;
      if (typeof detail.error === 'string' && detail.error.trim().length > 0) return detail.error;
    }
  }
  return fallback;
};

const toAbsoluteUrl = (pathOrUrl: string, baseUrl: string): string =>
  /^https?:\/\//i.test(pathOrUrl) ? pathOrUrl : `${baseUrl}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;

export const hasProtectedAdminAccess = (): boolean => {
  try {
    getAccessState();
    return true;
  } catch {
    return false;
  }
};

export const getProtectedAdminHeaders = (extraHeaders: HeadersInit = {}): Headers => {
  const { apiKey } = getAccessState();
  return mergeHeaders(
    getAuthHeaders({ 'x-api-key': apiKey }),
    extraHeaders
  );
};

export const protectedAdminFetch = async (
  pathOrUrl: string,
  options: RequestInit = {}
): Promise<Response> => {
  const { baseUrl } = getAccessState();
  const headers = getProtectedAdminHeaders(options.headers || {});
  return await authenticatedFetch(toAbsoluteUrl(pathOrUrl, baseUrl), {
    ...options,
    credentials: 'include',
    headers,
  });
};

export const throwIfProtectedAdminResponseNotOk = async (
  response: Response,
  fallbackMessage: string
): Promise<never | void> => {
  if (response.ok) return;

  const payload = await parseResponseBody(response);
  if (response.status === 401) {
    throw new ProtectedAdminRequestError('Je bent niet ingelogd of je sessie is ongeldig.', {
      status: 401,
      reason: 'unauthenticated',
    });
  }
  if (response.status === 403) {
    throw new ProtectedAdminRequestError('Je hebt geen adminrechten voor deze actie.', {
      status: 403,
      reason: 'forbidden',
    });
  }

  throw new ProtectedAdminRequestError(extractMessage(payload, fallbackMessage), {
    status: response.status,
    reason: 'request_failed',
  });
};
